// Frame 07 — app shell, loaded (33:1087). Walkthrough beat 5, and the screen a
// signed-in user sees most.
//
// Structure, from the frame: `Shell/PageHeader`, then a three-across stat row
// (3 x 362.67 in a 1136 body), then the `MOST CONFUSING TERMS` card.
//
// The drawn frame's copy belongs to a divergence map, and two of its three
// tiles name aggregates this product has no per-org endpoint for. Rather than
// render a dash where a number is drawn, the tiles carry aggregates that are
// real for every org: document and term counts come from `GET /docs`,
// unexplained jargon from `GET /dashboards/{org}/sufficiency`. No tile shows a
// trend — there is no historical comparison endpoint, and a fabricated delta is
// worse than an absent one.

import { Button, ErrorState, PageHeader, StatRow, StatTile } from "../../ui";
import { hrefs } from "../../shell/hrefs";
import { useTopTerms } from "../../dashboard/api";
import { MostLookedUpCard } from "./MostLookedUpCard";
import { OverviewEmpty } from "./OverviewEmpty";
import { OverviewLoading } from "./OverviewLoading";
import { WINDOW_DAYS, docsVerdict, useDocs, useSufficiency } from "./api";
import "./overview.css";

export function OverviewScreen({ orgSlug }: { orgSlug: string }) {
  const docs = useDocs(orgSlug);
  const sufficiency = useSufficiency(orgSlug);
  const terms = useTopTerms(orgSlug, WINDOW_DAYS);
  const to = hrefs(orgSlug);

  if (docs.isPending || sufficiency.isPending || terms.isPending)
    return <OverviewLoading />;

  const header = (
    <PageHeader
      eyebrow="Overview"
      title="What is confusing your readers"
      description="Terms your readers hover, stall on, and abandon, ranked across every document published in this workspace."
      actions={
        <>
          <Button to={to.documents}>View documents</Button>
          <Button to={to.analytics} variant="primary">
            View analytics
          </Button>
        </>
      }
    />
  );

  // A failed list is a failure, never an empty workspace.
  if (docs.isError)
    return (
      <div className="maisight-overview">
        {header}
        <div className="maisight-overview-panel">
          <ErrorState
            headline="Could not load this workspace"
            detail={docs.error.message}
            action={
              <Button variant="primary" onClick={() => void docs.refetch()}>
                Try again
              </Button>
            }
          />
        </div>
      </div>
    );

  const verdict = docsVerdict(docs.data);
  if (verdict !== "has-documents")
    return (
      <div className="maisight-overview">
        {header}
        <OverviewEmpty
          kind={verdict}
          orgSlug={orgSlug}
          docCount={docs.data.total}
          reasons={sufficiency.data?.reasons ?? []}
        />
      </div>
    );

  const published = docs.data.items.filter(
    (doc) => doc.status === "published",
  ).length;
  const termCount = docs.data.items.reduce((sum, doc) => sum + doc.term_count, 0);
  const jargon = sufficiency.data?.unexplained_terms ?? null;

  return (
    <div className="maisight-overview">
      {header}
      <StatRow>
        <StatTile
          label="Documents"
          value={docs.data.total.toLocaleString()}
          detail={`${published} with a published glossary`}
        />
        <StatTile
          label="Terms tracked"
          value={termCount.toLocaleString()}
          detail={`across ${published} published documents`}
        />
        <StatTile
          label="Unexplained jargon"
          value={jargon === null ? "—" : jargon.toLocaleString()}
          detail={
            jargon === null
              ? "needs a license key for this workspace"
              : "used once, never defined"
          }
        />
      </StatRow>
      <MostLookedUpCard
        orgSlug={orgSlug}
        terms={terms.data ?? []}
        rankedTotal={sufficiency.data?.terms ?? null}
        failed={terms.isError}
      />
    </div>
  );
}
